import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { useCompradorSessao, sairParaLogin, rotaApp } from './util';

interface ArtigoResumo {
  id: string;
  slug: string;
  categoria: string;
  titulo: string;
  resumo: string | null;
  minutosLeitura: number;
}

function IconeCategoria({ categoria }: { categoria: string }) {
  const s = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };
  const c = categoria.toLowerCase();
  if (c.includes('reajuste') || c.includes('incc')) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M4 17l5-5 4 3 7-8" {...s} />
        <path d="M16 7h4v4" {...s} />
      </svg>
    );
  }
  if (c.includes('orçamento') || c.includes('orcamento') || c.includes('finan')) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <rect x="3" y="6" width="18" height="13" rx="2.5" {...s} />
        <path d="M3 10h18" {...s} />
      </svg>
    );
  }
  if (c.includes('obra')) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M5 20V9.5L12 4l7 5.5V20" {...s} />
        <path d="M9 20v-5h6v5" {...s} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" {...s} />
      <path d="M12 8v4.5l3 1.5" {...s} />
    </svg>
  );
}

/** Aprender (design aprovado): destaque no primeiro artigo + lista compacta. */
export function AprenderComprador() {
  const slug = useCompradorSessao();
  const [, navigate] = useLocation();
  const [corMarca, setCorMarca] = useState<string | null>(null);
  const [orgNome, setOrgNome] = useState('');
  const [artigos, setArtigos] = useState<ArtigoResumo[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.artigos.query({ orgSlug: slug }),
    ])
      .then(([m, a]) => {
        if (!vivo) return;
        setCorMarca(m.corMarca);
        setOrgNome(m.orgNome);
        setArtigos(a as ArtigoResumo[]);
      })
      .catch(() => sairParaLogin(slug, navigate))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  const [destaque, ...resto] = artigos;

  return (
    <AppShellComprador corMarca={corMarca} orgNome={orgNome}>
      <div data-testid="aprender-comprador">
        <h1 className="font-serif text-[26px] font-medium tracking-tight text-ink">Aprender</h1>
        <p className="mt-1 text-sm text-ink-3">Conteúdo curto para você decidir com tranquilidade.</p>

        {carregando ? (
          <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>
        ) : artigos.length === 0 ? (
          <p className="py-16 text-center text-sm text-ink-3">Os primeiros conteúdos chegam em breve.</p>
        ) : (
          <>
            {destaque && (
              <Link
                href={rotaApp(slug!, `/aprender/${destaque.slug}`)}
                className="card mt-5 block overflow-hidden"
                data-testid="artigo-destaque"
              >
                <div
                  className="relative flex h-36 items-end p-4"
                  style={{ background: 'linear-gradient(150deg, var(--brand), var(--brand-deep))' }}
                >
                  <span className="absolute right-4 top-4 text-white/50" aria-hidden="true">
                    <IconeCategoria categoria={destaque.categoria} />
                  </span>
                  <span className="rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold text-white">
                    {destaque.categoria}
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-serif text-[18px] font-medium leading-snug text-ink">{destaque.titulo}</div>
                  {destaque.resumo && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">{destaque.resumo}</p>}
                  <div className="mt-2 text-xs text-ink-4">{destaque.minutosLeitura} min de leitura</div>
                </div>
              </Link>
            )}

            <div className="mt-3 space-y-2.5">
              {resto.map((a) => (
                <Link
                  key={a.id}
                  href={rotaApp(slug!, `/aprender/${a.slug}`)}
                  className="card flex items-center gap-3.5 p-4"
                  data-testid="artigo-item"
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                    aria-hidden="true"
                  >
                    <IconeCategoria categoria={a.categoria} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[14.5px] font-semibold leading-snug text-ink">{a.titulo}</div>
                    <div className="mt-0.5 text-xs text-ink-4">{a.minutosLeitura} min de leitura</div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShellComprador>
  );
}
